import Vue from 'vue';
import _ from '@glittr/frontend-core/src/utils';
import queryBuilder from '@glittr/frontend-core/src/plugins/validation/queryBuilder';
import { IJSONSchema } from '@glittr/frontend-core/src/plugins/validation/IJSONSchema';
import validationPatterns from '@glittr/frontend-core/src/plugins/validation/validationPatterns';
import RegisterClassRequestModel from '../../generated/types/model/resource/RegisterClassRequestModel';
import RequestSummaryItemResourceViewModel from './RequestSummaryItemResourceViewModel';
import RequestSummaryResourceViewModel from './RequestSummaryResourceViewModel';
import SubjectAreaResourceViewModel from './SubjectAreaResourceViewModel';

export default class RegisterClassRequestViewModel extends RegisterClassRequestModel {
  protected transformToDTO(): void {
    this.dto.requestSummary = this.requestSummary.toDTO();
  }

  subjectAreaList: SubjectAreaResourceViewModel[] = [];

  registrationAnswersSummary: RequestSummaryItemResourceViewModel[] = [];

  marketingAnswersSummary: RequestSummaryItemResourceViewModel[] = [];

  nativeLanguage?: string;

  schemaInstitution: IJSONSchema = {
    ...queryBuilder.properties({
      companyName: { required: true },
      eMail: { required: true, pattern: validationPatterns.email },
      phoneNumber: { required: false, pattern: validationPatterns.phone },
      street: { required: true },
      postalCode: { required: true },
      city: { required: true },
      subjectArea: { required: true },
    }),
  };

  schema: IJSONSchema = {
    ...queryBuilder.properties({
      eventId: { required: true },
      contactAddress: {
        type: 'object',
        properties: {
          firstName: { required: true },
          lastName: { required: true },
          salutation: { required: true },
          eMail: { required: true, pattern: validationPatterns.email },
          eMailRepeat: { required: true, sameAs: 'eMail' },
          street: { required: true },
          postalCode: { required: true },
          city: { required: true },
          dateOfBirth: { required: true },
          mobilePhoneNumber: { required: true, pattern: validationPatterns.phone },
          phoneNumber: { pattern: validationPatterns.phone },
        },
      },
      acceptGdpr: {
        required: true,
        type: 'boolean',
        enum: [
          true,
        ],
      },
    }),
    allOf: [
      {
        if: {
          properties: {
            isForeignLanguage: {
              const: true,
            },
          },
        },
        then: {
          ...queryBuilder.properties({
            toccoNativeLanguageId: {
              required: true,
            },
            schoolEducationCountry: {
              required: true,
            },
          }),
        },
      },
      {
        if: {
          properties: {
            hasOtherDisabilities: {
              const: true,
            },
          },
        },
        then: {
          ...queryBuilder.properties({
            otherDisabilitiesComment: {
              required: true,
            },
          }),
        },
      },
    ],
  };

  get requestSummary() {
    return new RequestSummaryResourceViewModel().fromDTO({
      sections: [
        {
          title: Vue.$t('widget.registerCourse.sectionPersonalData'),
          items: [
            { label: Vue.$t('widget.registerCourse.gender'), displayValue: Vue.$tEnum('widget.registerCourse.genderEnum', this.contactAddress.salutation!) },
            { label: Vue.$t('widget.registerCourse.firstName'), displayValue: this.contactAddress.firstName },
            { label: Vue.$t('widget.registerCourse.lastName'), displayValue: this.contactAddress.lastName },
            { label: Vue.$t('widget.registerCourse.street'), displayValue: this.contactAddress.street },
            { label: Vue.$t('widget.registerCourse.postalCode'), displayValue: this.contactAddress.postalCode },
            { label: Vue.$t('widget.registerCourse.city'), displayValue: this.contactAddress.city },
            { label: Vue.$t('widget.registerCourse.dateOfBirth'), displayValue: Vue.$format.localDate(this.contactAddress.dateOfBirth!) },
          ],
        },
        {
          title: Vue.$t('widget.registerCourse.sectionCommunication'),
          items: [
            { label: Vue.$t('widget.registerCourse.phonePrivate'), displayValue: this.contactAddress.phoneNumber },
            { label: Vue.$t('widget.registerCourse.mobilePrivate'), displayValue: this.contactAddress.mobilePhoneNumber },
            { label: Vue.$t('widget.registerCourse.eMail'), displayValue: this.contactAddress.eMail },
            { label: Vue.$t('widget.registerCourse.differentCorrespondenceAddress'), displayValue: this.correspondenceAddress.isDifferent ? Vue.$t('app.yes') : Vue.$t('app.no') },
            { label: Vue.$t('widget.registerCourse.differentInvoiceAddress'), displayValue: this.invoiceAddress.isDifferent ? Vue.$t('app.yes') : Vue.$t('app.no') },
          ],
        },
        {
          title: Vue.$t('widget.registerCourse.sectionCorrespondenceAddress'),
          items: [
            { label: Vue.$t('widget.registerCourse.companyName'), displayValue: this.correspondenceAddress.contactAddress.companyName },
            { label: Vue.$t('widget.registerCourse.street'), displayValue: this.correspondenceAddress.contactAddress.street },
            { label: Vue.$t('widget.registerCourse.postalCode'), displayValue: this.correspondenceAddress.contactAddress.postalCode },
            { label: Vue.$t('widget.registerCourse.city'), displayValue: this.correspondenceAddress.contactAddress.city },
            { label: Vue.$t('widget.registerCourse.phoneBusiness'), displayValue: this.correspondenceAddress.contactAddress.phoneNumber },
            { label: Vue.$t('widget.registerCourse.eMail'), displayValue: this.correspondenceAddress.contactAddress.eMail },
          ],
        },
        {
          title: Vue.$t('widget.registerCourse.sectionInvoiceAddress'),
          items: [
            { label: Vue.$t('widget.registerCourse.companyName'), displayValue: this.invoiceAddress.contactAddress.companyName },
            { label: Vue.$t('widget.registerCourse.street'), displayValue: this.invoiceAddress.contactAddress.street },
            { label: Vue.$t('widget.registerCourse.postalCode'), displayValue: this.invoiceAddress.contactAddress.postalCode },
            { label: Vue.$t('widget.registerCourse.city'), displayValue: this.invoiceAddress.contactAddress.city },
            { label: Vue.$t('widget.registerCourse.phoneBusiness'), displayValue: this.invoiceAddress.contactAddress.phoneNumber },
            { label: Vue.$t('widget.registerCourse.eMail'), displayValue: this.invoiceAddress.contactAddress.eMail },
            { label: Vue.$t('widget.registerCourse.eMail'), displayValue: this.invoiceAddress.contactAddress.eMail },
            { label: Vue.$t('widget.registerCourse.subjectArea'), displayValue: this.subjectAreaList.filter((x) => this.correspondenceAddress.contactAddress.subjectArea?.some((y) => x.id === y)).map((x) => x.caption).join(', ') },

          ],
        },
        {
          title: Vue.$t('widget.registerCourse.sectionCompensation'),
          items: [
            { label: Vue.$t('widget.registerCourse.compensation.hasADHD'), displayValue: this.hasAdhd ? Vue.$t('app.yes') : Vue.$t('app.no') },
            { label: Vue.$t('widget.registerCourse.compensation.hasDyslexia'), displayValue: this.hasDyslexia ? Vue.$t('app.yes') : Vue.$t('app.no') },
            { label: Vue.$t('widget.registerCourse.compensation.isForeignLanguage'), displayValue: this.isForeignLanguage ? Vue.$t('app.yes') : Vue.$t('app.no') },
            { label: Vue.$t('widget.registerCourse.compensation.foreignLanguage'), displayValue: this.nativeLanguage },
            { label: Vue.$t('widget.registerCourse.compensation.countryEducatedIn'), displayValue: this.schoolEducationCountry },
            { label: Vue.$t('widget.registerCourse.compensation.hasHearingImpairment'), displayValue: this.hasHearingImpairment ? Vue.$t('app.yes') : Vue.$t('app.no') },
            { label: Vue.$t('widget.registerCourse.compensation.hasVisualImpairment'), displayValue: this.hasVisualImpairment ? Vue.$t('app.yes') : Vue.$t('app.no') },
            { label: Vue.$t('widget.registerCourse.compensation.hasOtherImpairment'), displayValue: this.hasOtherDisabilities ? Vue.$t('app.yes') : Vue.$t('app.no') },
            { label: Vue.$t('widget.registerCourse.compensation.otherImpairment'), displayValue: this.otherDisabilitiesComment },
          ],
        },
        {
          title: Vue.$t('widget.registerCourse.sectionSpecificQuestions'),
          items: this.registrationAnswersSummary.map((answer) => answer?.toDTO()),
        },
        {
          title: Vue.$t('widget.registerCourse.sectionAdditionalInformation'),
          items: [
            // { label: Vue.$t('widget.registerCourse.comment'), displayValue: this.comment },
            ...this.marketingAnswersSummary.map((answer) => answer?.toDTO()),
            { label: Vue.$t('widget.registerCourse.acceptNewsletter'), displayValue: this.acceptNewsletter ? Vue.$t('app.yes') : Vue.$t('app.no') },
            { label: Vue.$t('widget.registerCourse.acceptGdpr'), displayValue: this.acceptGdpr ? Vue.$t('app.yes') : Vue.$t('app.no') },
          ],
        },
      ].map((section) => ({
        title: section.title,
        // Needs to be cast to any because build fails otherwise (and the dev build doesn't)
        items: (section.items as any).filter((item: any) => _.isSet(item.displayValue)),
      })).filter((section) => section.items.length > 0),
    });
  }
}
